import { useState } from 'react'
import { useEntryMutations } from '../hooks/useApi'
import { useT } from '../i18n/context'
import { errText } from '../lib/errors'
import { fromLocalInputValue, toLocalInputValue } from '../lib/format'
import { ABSENCE_TYPES, TRACK_TYPES, entryTypeMeta } from '../lib/labelMaps'
import { toast } from '../stores/toast'
import type { TimeEntry, TimeEntryType } from '../types/api'
import { ProjectSelect } from './ProjectSelect'
import { Button } from './ui/Button'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { DateTimeInput } from './ui/DateTimeInputs'
import { Dialog } from './ui/Dialog'
import { Field, Input, Select } from './ui/Field'

/** Vollständiger datetime-local-Wert ('yyyy-MM-ddTHH:mm'). */
const isComplete = (value: string) =>
    /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)

/**
 * Gemeinsamer Eintrags-Dialog. Bearbeiten (entry gesetzt): alle Typen,
 * leeres Ende erhält den laufenden Eintrag — wie bisher die Einträge-
 * Tabelle. Erstellen (entry null): Datum vorbelegt, nur Arbeits-Typen;
 * Abwesenheiten laufen über den AbsenceDialog, der Volltag/halber Tag/
 * Stunden mit Soll-Deckelung abbucht (KONZEPT 4.4).
 */
export function EntryDialog({
    open,
    onClose,
    workspaceId,
    userId,
    initialDate,
    entry = null,
}: {
    open: boolean
    onClose: () => void
    workspaceId: number
    /** OWNER/ADMIN: Eintrag für ein anderes Mitglied (sonst selbst). */
    userId?: number
    /** YYYY-MM-DD — belegt im Erstellen-Modus das Datum von/bis vor. */
    initialDate?: string
    entry?: TimeEntry | null
}) {
    const t = useT()
    const { create, update, remove } = useEntryMutations(workspaceId)
    const [description, setDescription] = useState('')
    const [projectId, setProjectId] = useState<number | null>(null)
    const [clientId, setClientId] = useState<number | null>(null)
    const [entryType, setEntryType] = useState<TimeEntryType>('WORK')
    const [isBillable, setIsBillable] = useState(true)
    const [start, setStart] = useState('')
    const [end, setEnd] = useState('')
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [initializedFor, setInitializedFor] = useState<string | null>(null)

    const editing = entry !== null
    const openKey = open
        ? editing
            ? `edit-${entry.id}`
            : `create-${initialDate ?? ''}`
        : null
    if (openKey !== null && initializedFor !== openKey) {
        setInitializedFor(openKey)
        setConfirmDelete(false)
        if (entry) {
            setDescription(entry.description)
            setProjectId(entry.projectId)
            setClientId(entry.clientId)
            setEntryType(entry.entryType)
            setIsBillable(entry.isBillable)
            setStart(toLocalInputValue(entry.startTime))
            setEnd(entry.endTime ? toLocalInputValue(entry.endTime) : '')
        } else {
            setDescription('')
            setProjectId(null)
            setClientId(null)
            setEntryType('WORK')
            setIsBillable(true)
            // Datum beidseitig vorbelegen (gleicher Tag), nur die Uhrzeit
            // bleibt offen — DateTimeInput emittiert erst vollständige Werte.
            setStart(initialDate ? `${initialDate}T` : '')
            setEnd(initialDate ? `${initialDate}T` : '')
        }
    }

    const saveDisabled =
        !isComplete(start) || (!editing && !isComplete(end))
    const saving = editing ? update.isPending : create.isPending

    // Abwesenheiten sind serverseitig nie fakturierbar — Checkbox wird
    // dafür gar nicht erst angeboten (KONZEPT 4.4)
    const absence = ABSENCE_TYPES.has(entryType)

    const submit = (e: React.FormEvent) => {
        e.preventDefault()
        if (saveDisabled) return
        if (isComplete(end) && new Date(end) < new Date(start)) {
            toast.error(t('track.endBeforeStart'))
            return
        }
        if (editing) {
            update.mutate(
                {
                    id: entry.id,
                    description,
                    projectId,
                    clientId,
                    entryType,
                    isBillable: absence ? false : isBillable,
                    startTime: fromLocalInputValue(start),
                    endTime: end ? fromLocalInputValue(end) : null,
                },
                {
                    onSuccess: () => {
                        toast.success(t('entries.entryUpdated'))
                        onClose()
                    },
                    onError: err => toast.error(errText(err)),
                },
            )
        } else {
            create.mutate(
                {
                    description,
                    projectId: projectId ?? undefined,
                    clientId: clientId ?? undefined,
                    entryType,
                    isBillable: absence ? false : isBillable,
                    startTime: fromLocalInputValue(start),
                    endTime: fromLocalInputValue(end),
                    ...(userId !== undefined ? { userId } : {}),
                },
                {
                    onSuccess: () => {
                        toast.success(t('track.entrySaved'))
                        onClose()
                    },
                    onError: err => toast.error(errText(err)),
                },
            )
        }
    }

    const typeOptions = editing
        ? Object.entries(entryTypeMeta)
        : TRACK_TYPES.map(value => [value, entryTypeMeta[value]] as const)

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={editing ? t('entries.editTitle') : t('entries.createTitle')}
        >
            <form onSubmit={submit} className='space-y-3'>
                <Field label={t('track.description')}>
                    <Input
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                    />
                </Field>
                <Field label={t('track.projectClient')}>
                    <ProjectSelect
                        value={{ projectId, clientId }}
                        onChange={(p, c) => {
                            setProjectId(p)
                            setClientId(c)
                        }}
                    />
                </Field>
                <div className='grid grid-cols-2 gap-3'>
                    <Field label={t('track.type')}>
                        <Select
                            value={entryType}
                            onChange={e =>
                                setEntryType(e.target.value as TimeEntryType)
                            }
                        >
                            {typeOptions.map(([value, meta]) => (
                                <option key={value} value={value}>
                                    {t(meta.labelKey)}
                                </option>
                            ))}
                        </Select>
                    </Field>
                    {!absence && (
                        <label className='flex items-end gap-2 pb-2 text-sm text-slate-600 dark:text-slate-300'>
                            <input
                                type='checkbox'
                                checked={isBillable}
                                onChange={e => setIsBillable(e.target.checked)}
                                className='h-4 w-4 rounded border-slate-300 dark:border-slate-600 accent-indigo-700 dark:accent-indigo-500'
                            />
                            {t('track.billable')}
                        </label>
                    )}
                </div>
                <div className='grid grid-cols-2 gap-3'>
                    <Field label={t('track.start')}>
                        <DateTimeInput value={start} onChange={setStart} />
                    </Field>
                    <Field
                        label={
                            editing
                                ? t('entries.endRunningHint')
                                : t('track.end')
                        }
                    >
                        <DateTimeInput value={end} onChange={setEnd} />
                    </Field>
                </div>
                <div
                    className={`flex pt-1 ${
                        editing ? 'justify-between' : 'justify-end'
                    }`}
                >
                    {editing && (
                        <Button
                            type='button'
                            variant='danger'
                            onClick={() => setConfirmDelete(true)}
                        >
                            {t('common.delete')}
                        </Button>
                    )}
                    <div className='flex gap-2'>
                        <Button
                            type='button'
                            variant='secondary'
                            onClick={onClose}
                        >
                            {t('common.cancel')}
                        </Button>
                        <Button
                            type='submit'
                            loading={saving}
                            disabled={saveDisabled && !saving}
                        >
                            {t('common.save')}
                        </Button>
                    </div>
                </div>
            </form>
            <ConfirmDialog
                open={confirmDelete}
                message={t('entries.deleteConfirm')}
                confirmLabel={t('common.delete')}
                loading={remove.isPending}
                onClose={() => setConfirmDelete(false)}
                onConfirm={() => {
                    if (!entry) return
                    remove.mutate(entry.id, {
                        onSuccess: () => {
                            toast.success(t('entries.entryDeleted'))
                            setConfirmDelete(false)
                            onClose()
                        },
                        onError: err => toast.error(errText(err)),
                    })
                }}
            />
        </Dialog>
    )
}
